'use client';
import { motion } from 'framer-motion';

import Image from 'next/image';
import Link from 'next/link';
import { COMPANY_INFO } from '@/lib/constants';
import { components } from '@/types/strapi';
type FooterLogo = components['schemas']['Logo'];
interface Logos extends FooterLogo {
	logo: components['schemas']['Logo'];
}

const FooterSection = ({
	title,
	items,
}: {
	title: string;
	items: components['schemas']['SharedLinkComponent'][];
}) => (
	<div className="mb-8 md:mb-0">
		<p className="mb-4 border-b border-gray-600 pb-2 text-lg font-semibold text-white">
			{title}
		</p>
		<ul className="space-y-2">
			{items?.map(item => (
				<li key={item.id}>
					<Link
						href={item.URL || '/'}
						className="group flex items-center text-center text-gray-300 transition-colors duration-200 hover:text-white">
						<span className="mr-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
							•
						</span>
						{item.text}
					</Link>
				</li>
			))}
		</ul>
	</div>
);

export default function Footer({
	logo,
	trust_seal,
	copyright,
	contact_information,
	social_media_links,
	internal_links,
}: {
	logo: Logos['logo'];
	trust_seal: Logos['logo'];
	copyright: string;
	contact_information: components['schemas']['ItemsFeatureComponent'][];
	social_media_links: components['schemas']['ItemsFeatureComponent'][];
	internal_links: components['schemas']['ItemsFooterCategoryComponent'][];
}) {
	return (
		<footer className="relative overflow-hidden bg-gray-900 py-8 text-white">
			<div className="absolute inset-0 bg-[url('/footer-bg-pattern.svg')] opacity-5"></div>
			<div className="container relative z-10 mx-auto px-4">
				<div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
					<div className="col-span-1 space-y-8 md:col-span-2 lg:col-span-1">
						{logo && logo.image && logo.image.url && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}>
								<Image
									src={logo.image.url}
									alt={logo.image.name || 'Footer Logo'}
									width={186}
									height={37}
									className="h-12 w-auto"
									priority
								/>
							</motion.div>
						)}

						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="space-y-4">
							{contact_information.map((feature, ind) => (
								<p
									key={ind}
									className="flex items-start gap-3 text-gray-300 hover:text-white">
									{feature.icon?.url && (
										<Image
											src={feature.icon?.url}
											alt={
												feature.icon?.name ||
												'Aternative text'
											}
											width={21}
											height={21}
											className="text-blue-500"
											priority
										/>
									)}
									<Link
										className="-mt-[0.5]"
										href={feature?.href || '/'}>
										{feature.text}
									</Link>
								</p>
							))}
						</motion.div>
						{trust_seal &&
							trust_seal.image &&
							trust_seal.image.url && (
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5 }}>
									<Image
										src={trust_seal.image.url}
										alt={
											trust_seal.image.name ||
											'Trust seal'
										}
										width={186}
										height={37}
										className="h-25 w-auto"
										priority
									/>
								</motion.div>
							)}
					</div>
					{internal_links &&
						internal_links.map((link, ind) => (
							<FooterSection
								key={ind}
								title={link.title || ''}
								items={link.items || []}
							/>
						))}
				</div>

				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.8 }}
					className="mt-12 flex flex-col items-center justify-between border-t border-gray-700 pt-8 align-middle md:flex-row">
					{copyright !== '' ? (
						<p className="mb-4 text-sm text-gray-400 md:mb-0">
							{copyright}
						</p>
					) : (
						<p className="mb-4 text-sm text-gray-400 md:mb-0">
							&copy; {new Date().getFullYear()}{' '}
							{COMPANY_INFO.name}. All rights reserved.
						</p>
					)}
					<div className="flex space-x-4">
						{social_media_links?.map((item, ind) => (
							<Link
								key={ind}
								href={item.href || '/'}
								className="rounded-full bg-gray-800 p-2 text-gray-400 transition-colors duration-200 hover:bg-gray-700 hover:text-white">
								{item.icon?.url && (
									<Image
										src={item.icon.url}
										alt={item.icon.name || 'Social icon'}
										width={21}
										height={21}
										priority
									/>
								)}
							</Link>
						))}
					</div>
				</motion.div>
			</div>
		</footer>
	);
}
